<template>
  <slide-in-modal
    class="memory-book-panel"
    :close-label="closeLabel"
    :disabled="false"
    :modal-header-desc="step === 'preview' ? texts.intro : ''"
    :modal-heading="texts.title"
    :modal-open="bookPanel.open"
    :toggle-modal="close"
  >
    <!-- After Stripe: one receipt card, the cover first, centred in the panel. -->
    <div class="memory-book-panel__done" v-if="step === 'done'">
      <div class="memory-book-panel__receipt">
        <img
          class="memory-book-panel__receipt-cover"
          v-if="receiptCover"
          :alt="texts.coverAlt"
          :src="receiptCover"
        >

        <p
          class="memory-book-panel__confirming"
          role="status"
          v-if="status === 'checking'"
        >
          <span aria-hidden="true" class="memory-book-panel__spinner" />
          {{ texts.confirming }}
        </p>

        <template v-else-if="status === 'paid'">
          <h3 class="memory-book-panel__done-title" role="status">
            <svg aria-hidden="true" class="icon icon--2x">
              <use href="#circle-check" />
            </svg>
            {{ texts.doneTitle }}
          </h3>
          <ul class="memory-book-panel__summary">
            <li v-for="line in receiptLines" :key="line" v-text="line" />
          </ul>
          <div class="memory-book-panel__receipt-actions">
            <a
              class="btn btn-cta"
              v-if="appLink"
              :href="appLink"
              @click="trackBook({ action: 'app_link' })"
            >
              {{ texts.appLink }}
            </a>
            <button class="btn obituary" type="button" @click="close">
              {{ closeLabel }}
            </button>
          </div>
        </template>

        <template v-else>
          <p role="status" v-text="notPaidText" />
          <div class="memory-book-panel__receipt-actions">
            <button
              class="btn btn-cta"
              type="button"
              v-if="status !== 'pending' && book"
              @click="backToPreview"
            >
              {{ texts.tryAgain }}
            </button>
            <button class="btn obituary" type="button" @click="close">
              {{ closeLabel }}
            </button>
          </div>
        </template>
      </div>
    </div>

    <div class="memory-book-panel__body" v-else-if="book">
      <template v-if="step === 'preview'">
        <!-- The book to turn, two pages at a time unless the panel is narrow. -->
        <div class="memory-book-panel__preview" ref="previewBox">
          <memory-book-flip
            class="memory-book-panel__book"
            v-if="bookPanel.open"
            :book="book"
            :key-order="KEY_ORDER.panel"
            :keys-active="() => bookPanel.open"
            :section-id="bookPanel.sectionId"
            :single="narrow"
            :size="narrow ? 'small' : 'normal'"
          />
        </div>

        <fieldset class="memory-book-panel__sizes">
          <legend class="memory-book-panel__label" v-text="texts.size" />
          <label
            class="memory-book-panel__size"
            v-for="option in book.sizes"
            :key="option.size"
          >
            <input
              name="memory-book-size"
              type="radio"
              v-model="size"
              :value="option.size"
              @change="trackBook({ action: 'size', size: option.size })"
            >
            <span class="memory-book-panel__size-box">
              <span aria-hidden="true" class="memory-book-panel__size-check">
                <svg aria-hidden="true" viewBox="0 0 448 512">
                  <use href="#check" />
                </svg>
              </span>
              <span class="memory-book-panel__size-name">
                {{ sizeName(option) }}
              </span>
              <span class="memory-book-panel__size-price">
                <template v-if="discounted(option) !== null">
                  <s v-text="formatPrice(option.unitPrice, book.currency)" />
                  {{ formatPrice(discounted(option) ?? 0, book.currency) }}
                </template>
                <template v-else>
                  {{ formatPrice(option.unitPrice, book.currency) }}
                </template>
              </span>
            </span>
          </label>
        </fieldset>

        <memory-book-coupon
          ref="couponField"
          v-model="coupon"
          :currency="book.currency"
          :order-id="orderId"
        />

        <ul class="memory-book-panel__facts">
          <li v-if="delivery">
            <svg aria-hidden="true" viewBox="0 0 448 512">
              <use href="#calendar-days" />
            </svg>
            <span v-text="delivery" />
          </li>
          <li>
            <span aria-hidden="true" class="memory-book-panel__card-icon" />
            <span v-text="texts.payWith" />
          </li>
        </ul>

        <p class="memory-book-panel__next" v-text="texts.nextStep" />

        <p
          class="memory-book-panel__shipping"
          v-if="shipping"
          v-text="shipping"
        />

        <!-- The site's own consent line with its links, as in the memorial gift. -->
        <p class="memory-book-panel__terms" v-html="texts.terms" />

        <div class="memory-book-panel__footer">
          <p
            class="slide-in-modal__error-message"
            role="alert"
            v-if="failure"
            v-text="failure"
          />
          <button
            class="btn btn-cta memory-book-panel__pay"
            type="button"
            :disabled="opening"
            @click="toCheckout"
          >
            {{ opening ? texts.opening : payLabel }}
          </button>
        </div>
      </template>

      <template v-else>
        <!-- Step 2 is Stripe's form; the size can still be changed. -->
        <div class="memory-book-panel__step">
          <span v-text="texts.stepPayment" />
          <button
            class="memory-book-panel__back"
            type="button"
            @click="backToPreview"
          >
            <svg aria-hidden="true" viewBox="0 0 320 512">
              <use href="#chevron-left" />
            </svg>
            {{ texts.back }}
          </button>
        </div>
        <p
          class="memory-book-panel__confirming"
          role="status"
          v-if="!checkoutShown"
        >
          <span aria-hidden="true" class="memory-book-panel__spinner" />
          {{ texts.opening }}
        </p>
        <div class="memory-book-panel__checkout" ref="checkoutEl" />
      </template>
    </div>
  </slide-in-modal>
</template>

<script lang="ts" setup>
  import SlideInModal from "Components/SlideInModal.vue";
  import type { StripeEmbeddedCheckout } from "@stripe/stripe-js";
  import { sprintf } from "sprintf-js";
  import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from "vue";
  import { KEY_ORDER } from "../../highlights/book-keys";
  import {
    type BookCoupon,
    type BookSize,
    type BookSizeId,
    type CheckoutStatus,
    type CouponErrorCode,
    formatPrice,
    PurchaseError,
    type PurchaseErrorCode,
    type Receipt,
    sizeInCm,
    startPurchase,
    takeReturnedSession,
    waitForPayment,
  } from "../../highlights/memory-book";
  import {
    bookPanel,
    closeBookPanel,
    openBookPanel,
    reloadMemoryBook,
  } from "../../highlights/memory-book-panel";
  import { mountCheckout } from "../../highlights/stripe-checkout";
  import { commonTexts, memoryBookTexts } from "../../highlights/texts";
  import { trackBook } from "../../slideshow/track";
  import MemoryBookCoupon from "./MemoryBookCoupon.vue";
  import MemoryBookFlip from "./MemoryBookFlip.vue";

  declare const lavendlaObituary: { backendAPIUrl: string };

  /**
   * The memory book's slide-in panel: look through the book and pick a size, then Stripe's
   * embedded Checkout asks how many, the address and the email. Stripe sends the payer back
   * to the page with the session, and the panel opens again on the confirmation.
   */
  const props = defineProps<{ orderId: string }>();

  type Step = "preview" | "checkout" | "done";

  const MM_SUFFIX = / ?mm$/;
  const texts = memoryBookTexts();
  const closeLabel = commonTexts().close;
  const book = computed(() => bookPanel.book);
  /** Below this width the panel's book shows one page at a time. */
  const NARROW_PX = 420;
  const previewBox = ref<HTMLElement | null>(null);
  const narrow = ref(window.innerWidth < 768);
  let previewObserver: ResizeObserver | null = null;
  watch(previewBox, (el) => {
    previewObserver?.disconnect();
    if (el && typeof ResizeObserver !== "undefined") {
      previewObserver = new ResizeObserver(() => {
        if (el.clientWidth) {
          narrow.value = el.clientWidth < NARROW_PX;
        }
      });
      previewObserver.observe(el);
    }
  });
  onBeforeUnmount(() => previewObserver?.disconnect());
  const step = ref<Step>("preview");
  const size = ref<BookSizeId>("S");
  const opening = ref(false);
  const failure = ref("");
  const status = ref<CheckoutStatus | "checking">("checking");
  const appLink = ref("");
  const receipt = ref<Receipt | null>(null);
  const checkoutEl = ref<HTMLElement | null>(null);
  const checkoutShown = ref(false);
  const coupon = ref<BookCoupon | null>(null);
  const couponField = ref<{ refused: (error: CouponErrorCode) => void } | null>(
    null
  );
  let checkout: StripeEmbeddedCheckout | null = null;

  const ERRORS: Record<PurchaseErrorCode, string> = {
    checkout_unavailable: texts.errorFailed,
    coupon_invalid: texts.couponUnavailable,
    failed: texts.errorFailed,
    not_for_sale: texts.errorNotForSale,
    rate_limited: texts.errorRateLimited,
    share_changed: texts.errorShareChanged,
  };

  /** "Mindre, 20 × 20 cm": the app's names for the two sizes. */
  function sizeName(option: BookSize): string {
    const name = option.size === "S" ? texts.sizeSmall : texts.sizeLarge;
    return `${name}, ${sizeInCm(option.labelMm)} cm`;
  }

  const receiptCover = computed(
    () => book.value?.cover.url ?? receipt.value?.coverUrl ?? ""
  );

  const NOT_PAID: Record<string, string> = {
    expired: texts.doneExpired,
    pending: texts.waiting,
    unknown: texts.doneUnknown,
  };
  const notPaidText = computed(() => NOT_PAID[status.value] ?? "");

  /** The thank-you's summary: only what the status answer gave, then the delivery estimate. */
  const receiptLines = computed(() => {
    const paid = receipt.value;
    const current = book.value;
    const option = current?.sizes.find((o) => o.size === paid?.size);
    const currency = paid?.currency ?? current?.currency;
    // The receipt's own label when the book's card is not loaded on the way back.
    const fromLabel =
      paid?.size && paid.sizeLabel
        ? sizeName({
            labelMm: paid.sizeLabel.replace(MM_SUFFIX, ""),
            size: paid.size,
            unitPrice: 0,
          })
        : "";
    const days = paid?.deliveryDays ?? current?.deliveryDays;
    const lines = [
      option ? sizeName(option) : fromLabel,
      paid?.copies ? sprintf(texts.receiptCopies, paid.copies) : "",
      paid?.total && currency
        ? sprintf(texts.receiptTotal, formatPrice(paid.total, currency))
        : "",
      paid?.city ? sprintf(texts.shipTo, paid.city) : "",
      paid?.emailMasked
        ? sprintf(texts.receiptTo, paid.emailMasked)
        : texts.doneText,
      days?.min && days.max ? sprintf(texts.delivery, days.min, days.max) : "",
    ];
    return lines.filter(Boolean);
  });

  /** One copy's price after a valid percentage code, as the API answered it; null otherwise. */
  function discounted(option: BookSize): number | null {
    const current = coupon.value;
    return current && !current.error
      ? (current.unitPrices[option.size] ?? null)
      : null;
  }

  // An amount off the order has no price per copy: Stripe shows the total.
  const payLabel = computed(() => {
    if (!(book.value && chosen.value)) {
      return "";
    }
    if (coupon.value?.type === "amount" && !coupon.value.error) {
      return texts.toPaymentPlain;
    }
    return sprintf(
      texts.toPayment,
      formatPrice(
        discounted(chosen.value) ?? chosen.value.unitPrice,
        book.value.currency
      )
    );
  });

  const chosen = computed(() =>
    book.value?.sizes.find((option) => option.size === size.value)
  );

  const delivery = computed(() => {
    const days = book.value?.deliveryDays;
    return days?.min && days.max
      ? sprintf(texts.delivery, days.min, days.max)
      : "";
  });

  // A delivery fee is shown as a plain price; no fee says nothing.
  const shipping = computed(() => {
    const current = book.value;
    if (!current?.shipping) {
      return "";
    }
    return sprintf(
      texts.shipping,
      formatPrice(current.shipping, current.currency)
    );
  });

  function destroyCheckout() {
    checkout?.destroy();
    checkout = null;
    checkoutShown.value = false;
  }

  function backToPreview() {
    destroyCheckout();
    failure.value = "";
    step.value = "preview";
  }

  function close() {
    destroyCheckout();
    closeBookPanel();
  }

  async function toCheckout() {
    const current = book.value;
    if (!current || opening.value) {
      return;
    }
    opening.value = true;
    failure.value = "";
    try {
      const session = await startPurchase({
        apiUrl: lavendlaObituary.backendAPIUrl,
        // Only a code the API accepted; a refused one is shown, and the full price paid.
        couponCode:
          coupon.value && !coupon.value.error ? coupon.value.code : undefined,
        orderId: props.orderId,
        shareId: current.shareId,
        size: size.value,
      });
      step.value = "checkout";
      await nextTick();
      if (!checkoutEl.value) {
        return;
      }
      checkout = await mountCheckout(
        checkoutEl.value,
        session.publishableKey,
        session.clientSecret
      );
      checkoutShown.value = true;
      trackBook({ action: "checkout_shown", size: size.value });
    } catch (e) {
      const code = e instanceof PurchaseError ? e.code : "failed";
      trackBook({
        action: "checkout_failed",
        reason: e instanceof PurchaseError ? e.code : "stripe",
        size: size.value,
      });
      backToPreview();
      if (code === "coupon_invalid") {
        // The code ran out (or Stripe refused it) after it was checked: say so at the field.
        await nextTick();
        couponField.value?.refused(
          e instanceof PurchaseError && e.couponError
            ? e.couponError
            : "unavailable"
        );
        return;
      }
      failure.value = ERRORS[code];
      if (code === "share_changed" || code === "not_for_sale") {
        await reloadMemoryBook();
      }
    } finally {
      opening.value = false;
    }
  }

  async function confirmPayment(session: string) {
    step.value = "done";
    status.value = "checking";
    const result = await waitForPayment(
      lavendlaObituary.backendAPIUrl,
      session
    );
    status.value = result.status;
    appLink.value = result.status === "paid" ? result.appLink : "";
    receipt.value = result.status === "paid" ? result.receipt : null;
    if (result.status === "paid") {
      trackBook({ action: "paid" });
    }
  }

  // A fresh panel each time it opens from the card; the confirmation stays until closed.
  watch(
    () => bookPanel.open,
    (open, wasOpen) => {
      if (open && !wasOpen && !bookPanel.session) {
        backToPreview();
      }
      if (!open) {
        bookPanel.session = null;
      }
    }
  );

  watch(
    () => book.value?.sizes,
    (sizes) => {
      if (
        sizes?.length &&
        !sizes.some((option) => option.size === size.value)
      ) {
        size.value = sizes[0].size;
      }
    },
    { immediate: true }
  );

  onMounted(() => {
    const session = takeReturnedSession();
    if (!session) {
      return;
    }
    bookPanel.session = session;
    openBookPanel("return");
    confirmPayment(session);
  });
</script>
